import axios from "axios";
import { DataGrid } from "devextreme-react";
import {
  Column,
  ColumnChooser,
  Editing,
  FilterRow,
  Form,
  HeaderFilter,
  LoadPanel,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Texts,
} from "devextreme-react/data-grid";
import {
  AsyncRule,
  EmailRule,
  Label,
  RequiredRule,
  SimpleItem,
  StringLengthRule,
} from "devextreme-react/form";
import CustomStore from "devextreme/data/custom_store";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/hasPermission";
import { PERM } from "../../../utils/permissions";
import { notifyMsg } from "../../common/notify";

export default function ReviewerList(props) {
  const [dataSource, setDataSource] = useState(null);
  const [editRowKey, setEditRowKey] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);
  const canEditReviewer = hasPermission(userInfo.permissions, PERM.ORG_REVIEWER_EDIT);

  useEffect(() => {
    setDataSource(
      new CustomStore({
        key: "employeeNo",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/employee?jd=RV`
          );

          return result.data;
        },
        insert: async (values) => {
          const result = await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/employee/rv`,
            values
          );
          if (result.data?.profileCreated) {
            notifyMsg("기존에 없던 직책 조합입니다. 연결된 권한이 없으니 필요한 경우 권한을 새로 지정해 주세요.", "warning");
          }
        },

        update: async (key, values) => {
          const result = await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/employee/rv/${key}`,
            values
          );
          if (result.data?.profileCreated) {
            notifyMsg("기존에 없던 직책 조합입니다. 연결된 권한이 없으니 필요한 경우 권한을 새로 지정해 주세요.", "warning");
          }

          setEditRowKey(null);
        },
        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/employee/rv/${key}`
          );
        },
        errorHandler: (error) => {
          if (error.message == `Request failed with status code 403`) {
            error.message =
              "SOP에 대한 Audit Trails가 있습니다. 삭제가 불가능합니다.";
          } else if (error.message == `Request failed with status code 423`) {
            error.message =
              "BeTree Access Log가 있습니다. 삭제가 불가능합니다.";
          }
        },
      })
    );
  }, []);

  const calculateDisplayValue = (data) => {
    if (data.mob) {
      return `010-${data.mob.substring(0, 4)}-${data.mob.substring(4)}`;
    } else {
      return null;
    }
  };

  const validationCallbackEmail = async (params) => {
    const result = await axios.get(
      `${process.env.REACT_APP_BECCO_API_URL}/api/employee/hasReviewer/${editRowKey}?email=${params.value}`
    );

    return result.data.nEmployeeNo == 0;
  };

  const validationCallback = async (params) => {
    if (!editRowKey || editRowKey != params.value) {
      const result = await axios.get(
        `${process.env.REACT_APP_BECCO_API_URL}/api/employee/hasReviewer/${params.value}`
      );

      return result.data.nEmployeeNo == 0;
    } else {
      return true;
    }
  };

  const onEditingStart = (e) => {
    setEditRowKey(e.key);
  };

  const onEditCanceled = (e) => {
    setEditRowKey(null);
  };

  return (
    <DataGrid
      keyExpr={"employeeNo"}
      dataSource={dataSource}
      hoverStateEnabled={true}
      width={"100%"}
      height={"100%"}
      showBorders={true}
      showColumnHeaders={true}
      showColumnLines={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
      }}
      onEditingStart={onEditingStart}
      onEditCanceled={onEditCanceled}
    >
      <LoadPanel enabled={true} />

      <Scrolling mode={"virtual"} showScrollbar={"always"} />

      <Selection mode={"single"} />

      {/* <ColumnChooser enabled={true} /> */}

      <FilterRow visible={true} />
      <HeaderFilter visible={true} />

      <SearchPanel visible={true} />

      <StateStoring
        enabled={true}
        type={"localStorage"}
        storageKey={"Organization.ReviewerList"}
      />

      {canEditReviewer && (
        <Editing
          mode={"popup"}
          allowAdding={true}
          allowDeleting={true}
          allowUpdating={true}
        >
          <Texts
            confirmDeleteTitle={"Delete the Reviewer"}
            confirmDeleteMessage="Are you sure you want to delete this reviewer?"
          />
          <Popup
            // title={"Reviewer Info."}
            title={"REVIEWER INFO."}
            showTitle={true}
            height={"auto"}
          ></Popup>
          <Form
            labelMode="outside"
            showColonAfterLabel={false}
            labelLocation={"top"}
            minColWidth={300}
            colCount={2}
            colCountByScreen={{ sm: 2, md: 4 }}
            screenByWidth={(width) => {
              return width < 720 ? "sm" : "md";
            }}
            validationGroup="employee"
            height={"100%"}
          >
            <SimpleItem
              dataField={"employeeNo"}
              editorType={"dxTextBox"}
              editorOptions={{
                showClearButt: true,
              }}
            >
              <Label text={"No."} />
              <RequiredRule message={"* No.를 입력해주세요."} />
              <StringLengthRule
                min={8}
                max={8}
                message="Character Length of No. is 8."
              />
              <AsyncRule
                message={"No. is alreay registered."}
                validationCallback={validationCallback}
                // reevaluate={false}
              />
            </SimpleItem>
            <SimpleItem
              dataField={"name"}
              editorType="dxTextBox"
              editorOptions={{
                showClearButton: true,
              }}
            >
              <Label text={"Name"} />
              <RequiredRule message={"* Name을 입력해주세요."} />
            </SimpleItem>
            <SimpleItem
              dataField={"startDate"}
              editorType="dxDateBox"
              editorOptions={{
                type: "date",
                displayFormat: "yyyy-MM-dd",
                dateSerializationFormat: "yyyy-MM-dd",
                showClearButton: true,
                min: new Date("2022-04-01"),
              }}
            >
              <Label text={"Start Date"} />
              <RequiredRule message={"* Start date를 입력해주세요."} />
            </SimpleItem>
            <SimpleItem
              dataField={"depDate"}
              editorType="dxDateBox"
              editorOptions={{
                type: "date",
                displayFormat: "yyyy-MM-dd",
                dateSerializationFormat: "yyyy-MM-dd",
                showClearButton: true,
                // min: new Date("2022-04-01"),
              }}
            >
              <Label text={"End Date"} />
              <RequiredRule message={"* End date를 입력해주세요."} />
            </SimpleItem>
            <SimpleItem
              dataField={"mob"}
              editorType="dxTextBox"
              editorOptions={{
                showClearButton: true,
                mask: "\\01\\0-0000-0000",
              }}
            >
              <RequiredRule message={"* Mobile를 입력해주세요."} />
            </SimpleItem>
            <SimpleItem
              dataField={"email"}
              editorOptions={{ showClearButton: true }}
            >
              <RequiredRule message={"* Email을 입력해주세요."} />
              <EmailRule message={"Email is invalid."} />
              <AsyncRule
                message={"Email is already registered."}
                validationCallback={validationCallbackEmail}
              />
            </SimpleItem>
          </Form>
        </Editing>
      )}

      <Column
        dataField={"employeeNo"}
        caption="No"
        width={140}
        alignment={"center"}
      />
      <Column
        dataField={"name"}
        caption="Name"
        width={100}
        alignment={"center"}
      />
      <Column
        dataField={"startDate"}
        caption={"Start Date"}
        dataType={"date"}
        format="yyyy-MM-dd"
        editorOptions={{ dateSerializationFormat: "yyyy-MM-dd" }}
        width={120}
        alignment="center"
      />
      <Column
        dataField={"depDate"}
        caption={"End Date"}
        dataType={"date"}
        format="yyyy-MM-dd"
        editorOptions={{ dateSerializationFormat: "yyyy-MM-dd" }}
        width={120}
        alignment="center"
      />
      <Column
        dataField={"mob"}
        caption="Mobile"
        width={120}
        alignment="center"
        calculateDisplayValue={calculateDisplayValue}
      />
      <Column dataField={"email"} caption="Email" width={250} />
    </DataGrid>
  );
}
